// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsFeaturesStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    specificationsWrapper?: InjectableCss;
}

export const featuresStyles: ProductDetailsFeaturesStyles = {
    wrapper: {
        css: css`
            padding: 32px 0;
            border-top: 1px solid ${color("common.ui7")};
        `,
    },
    title: {
        size: 28,
        weight: 800,
        color: "common.ui11",
        css: css`
            line-height: 34px;
            display: block;
            margin-bottom: 16px;
        `,
    },
    specificationsWrapper: {
        css: css`
            ul {
                display: flex;
                flex-direction: column;
                padding-left: 29px;
            }
        `,
    },
};

const styles = featuresStyles;

const ProductDetailsFeatures: React.FC<Props> = ({ product: { specifications } }) => {
    if (!specifications || !specifications.length) {
        return null;
    }

    const features = specifications.find(spec => spec.description?.toLowerCase() === "features");
    if (!features || !features.htmlContent) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography {...styles.title}>{translate("Features")}</Typography>
            <StyledWrapper {...styles.specificationsWrapper}>
                {parse(features.htmlContent, parserOptions)}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsFeatures),
    definition: {
        displayName: "Features",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
